// Parsing, wording and failure mapping for a one-off gift of credits.
// The backend accepts a JSON number that is a whole number of at least
// MIN_GIFT_CREDITS; a numeric string is refused, so the parser hands back a
// number and nothing else.

/** Mirrors MIN_GIFT_CREDITS in the backend's src/gifts/gifts.util.ts. */
export const MIN_GIFT_CREDITS = 5;

/** The one-tap amounts offered above the free-text field. */
export const QUICK_GIFT_AMOUNTS: readonly number[] = [5, 10, 25, 50];

export type GiftAmountResult =
  | { ok: true; credits: number }
  | { ok: false; reason: string };

/**
 * Turns what the fan typed into a whole number of credits.
 * Rejects blanks, decimals, signs, exponents and anything non-numeric so the
 * backend never sees a value it would answer with a 400.
 */
export function parseGiftAmount(raw: string): GiftAmountResult {
  const trimmed = raw.trim();

  if (!trimmed) {
    return { ok: false, reason: 'Enter an amount in credits.' };
  }

  if (!/^\d+$/.test(trimmed)) {
    return { ok: false, reason: 'Use a whole number of credits.' };
  }

  const credits = Number(trimmed);

  if (credits < MIN_GIFT_CREDITS) {
    return { ok: false, reason: `The smallest gift is ${MIN_GIFT_CREDITS} credits.` };
  }

  if (!Number.isSafeInteger(credits)) {
    return { ok: false, reason: 'That amount is too large.' };
  }

  return { ok: true, credits };
}

/** What the fan is about to spend, e.g. "You'll spend 25 credits". */
export function formatGiftSpend(credits: number): string {
  return `You'll spend ${credits} ${credits === 1 ? 'credit' : 'credits'}`;
}

/** Confirmation after a gift lands, e.g. "You sent Nova 25 credits". */
export function formatGiftSent(credits: number, creatorName: string): string {
  return `You sent ${creatorName} ${credits} ${credits === 1 ? 'credit' : 'credits'}`;
}

export interface GiftFailure {
  message: string;
  /** Show a link to /buy-credits: the only fix is topping up. */
  showBuyCredits: boolean;
}

/**
 * Turns a failed gift into something a fan can act on. Keyed on the backend's
 * own literal messages, and the catch-all never echoes the server string, so
 * neither a raw axios message nor a bare status code can reach the screen.
 */
export function giftFailure(
  status: number | undefined,
  serverMessage: string,
): GiftFailure {
  const trimmed = (serverMessage || '').trim();

  if (status === 400 && trimmed === 'Insufficient credits') {
    return {
      message: "You don't have enough credits to send this gift.",
      showBuyCredits: true,
    };
  }

  if (status === 400 && trimmed === `amountCredits must be a whole number of at least ${MIN_GIFT_CREDITS}`) {
    return {
      message: `The smallest gift is ${MIN_GIFT_CREDITS} credits.`,
      showBuyCredits: false,
    };
  }

  if (status === 400 && trimmed === 'You cannot gift yourself') {
    return {
      message: "You can't send yourself a gift.",
      showBuyCredits: false,
    };
  }

  if (status === 404 && trimmed === 'Creator not found') {
    return {
      message: "We couldn't find this creator.",
      showBuyCredits: false,
    };
  }

  if (status === 401) {
    return {
      message: 'Please log in again.',
      showBuyCredits: false,
    };
  }

  return {
    message: 'Something went wrong. Please try again.',
    showBuyCredits: false,
  };
}
